import moment from "moment";
import { currencies } from "./utils/const/currencies";
import {
  COMMISSION_TYPES,
  DATE_FORMATS,
  getDurationUnitsMap,
} from "./constants";
import {
  CommissionType,
  DurationUnit,
  Symbol,
  TGetBeautifiedDuration,
} from "./types";

export function env(name: string, defaultValue?: any) {
  return process.env[name] || defaultValue;
}

export function toCurrency(
  value,
  currencyId,
  separator = "",
  type: Symbol = "symbol",
) {
  if (isNaN(value)) {
    return value;
  }

  if (!currencyId) {
    return toNumber(value);
  }

  const currency = currencies.find((currency) => currency.id === currencyId);

  if (!currency) {
    return toNumber(value);
  }

  if (currency.pre) {
    return (
      currency[type] + separator + toNumber(value, currency.decimal_digits)
    );
  }

  return toNumber(value, currency.decimal_digits) + separator + currency[type];
}

export function toNumber(
  value: number | string,
  precision: number = 2,
): number | string {
  if (isNaN(value as number)) {
    return value;
  }

  value = (+value).toFixed(precision);

  return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function toBool(value: string): boolean {
  return value === "true";
}

function parseDotNotation(str, val, obj) {
  let currentObj = obj,
    keys = str.split("."),
    i,
    l = Math.max(1, keys.length - 1),
    key;

  for (i = 0; i < l; ++i) {
    key = keys[i];
    currentObj[key] = currentObj[key] || {};
    currentObj = currentObj[key];
  }

  currentObj[keys[i]] = val;
  delete obj[str];
}

export function parseRequestError(error) {
  let errors: any[] = [];
  if (
    typeof error.response !== "undefined" &&
    typeof error.response.data !== "undefined" &&
    typeof error.response.data.errors !== "undefined"
  ) {
    error.response.data.errors.forEach((error) =>
      errors.push({ message: error.message }),
    );
  } else {
    errors.push({ message: error.message });
  }
  return errors;
}

export function expand(obj) {
  Object.keys(obj).forEach((key) => {
    if (key.indexOf(".") !== -1) {
      parseDotNotation(key, obj[key], obj);
    }
  });
  return obj;
}

export function getCurrency(id: number, field: "name" | "icon" = "icon") {
  let currency = currencies.filter((item) => item.id == id);

  if (currency.length) return currency[0][field];
  return id;
}

export function formatDate(value, dateFormat = DATE_FORMATS.backEnd) {
  if (!value || value == "") return value;

  value = Date.parse(value);
  return moment(value, dateFormat);
}

export function getDataFromMap(map, key) {
  return map[key] || "";
}

export function truncate(str, start = 5, end = 5) {
  if (str == null) {
    return "";
  }
  if (str.length <= start + end) {
    return str;
  }
  return str.substring(0, start) + "..." + str.slice(str.length - end);
}

export function isValidURL(str) {
  const pattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$",
    "i",
  ); // fragment locator
  return !!pattern.test(str);
}

export function isStringsEqual(str1, str2) {
  const newStr1 = str1.replace(/\s+/g, "").toLowerCase();
  const newStr2 = str2.replace(/\s+/g, "").toLowerCase();

  return newStr1 === newStr2;
}

export function calculateIntakeName(date) {
  if (!date || (typeof date === "object" && !date.isValid())) {
    return "N/A";
  }

  const month =
    typeof date === "object"
      ? date.month()
      : moment(date, DATE_FORMATS.frontEnd).month();

  if (month === 11 || month <= 3) {
    return "Spring";
  }

  if (month >= 4 && month <= 6) {
    return "Summer";
  }

  if (month >= 7 && month <= 10) {
    return "Fall";
  }

  return "";
}

export function getCommissionType(id) {
  return COMMISSION_TYPES.find((type) => type.id == id)!.name;
}

export function calculateFinancialSupport(
  tuition: number,
  calculate: boolean,
  type: number | null,
  amount: number | null,
): string {
  if (calculate && !amount) {
    return "Is Available";
  }

  if (calculate && amount && type === CommissionType.FIXED) {
    return String(amount);
  }

  if (calculate && amount && type === CommissionType.PERCENT) {
    return ((tuition / 100) * amount).toFixed(2);
  }

  return "N/A";
}

export function calculateAllFinancialSupport(
  scholarship,
  financialAid,
  currency,
) {
  if (scholarship === "N/A" && financialAid === "N/A") {
    return "N/A";
  }

  if (scholarship > 0 && financialAid > 0) {
    return toCurrency(+scholarship + +financialAid, currency, " ");
  }

  if (scholarship > 0) {
    return toCurrency(scholarship, currency, " ");
  }

  if (financialAid > 0) {
    return toCurrency(financialAid, currency, " ");
  }

  return "Is Available";
}

export function urlNormalizer(url) {
  if (url && !url.startsWith("https:")) {
    return "//" + url;
  }

  return url;
}

export function isFileTypeImage(path: string): boolean {
  return (
    path.endsWith(".jpg") || path.endsWith(".jpeg") || path.endsWith(".png")
  );
}

export function isPositiveNumber(str) {
  return !isNaN(str) && +str > 0;
}

export const beautifyDurationUnit = (
  durationAmount: number | null,
  durationUnitId: DurationUnit,
  entityName: string,
): string => {
  if (durationUnitId === DurationUnit.FULL || durationAmount !== 1) {
    return getDurationUnitsMap(entityName)[durationUnitId];
  }

  switch (durationUnitId) {
    case DurationUnit.DAYS:
      return "Day";
    case DurationUnit.WEEKS:
      return "Week";
    case DurationUnit.MONTHS:
      return "Month";
    case DurationUnit.YEARS:
      return "Year";
    default:
      return "";
  }
};

export const getBeautifiedDuration: TGetBeautifiedDuration = (
  entityName,
  durationUnitId,
  durationAmount,
) => {
  const beautifiedDurationUnit = beautifyDurationUnit(
    +(durationAmount ?? 0),
    durationUnitId,
    entityName,
  ).toLowerCase();

  return durationAmount
    ? `${durationAmount} ${beautifiedDurationUnit}`
    : beautifiedDurationUnit;
};

export const getBeautifiedCount = (
  count: number,
  label: string,
  suffix: string = "s",
): string => {
  if (count === 0) {
    return `No ${label}${suffix}`;
  }

  if (count === 1) {
    return `1 ${label}`;
  }

  return count + ` ${label}${suffix}`;
};
